/** Refit video areas when the first decoded frame or a new native size arrives. */
export class VideoFrameObserver {
  private videos = new Map<
    string,
    { video: HTMLVideoElement; dispose: () => void }
  >();

  static isReady(video: HTMLVideoElement): boolean {
    return (
      video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
      video.videoWidth > 0 &&
      video.videoHeight > 0
    );
  }

  has(id: string, video: HTMLVideoElement): boolean {
    return this.videos.get(id)?.video === video;
  }

  isPending(id: string): boolean {
    const video = this.videos.get(id)?.video;
    return !!video && !VideoFrameObserver.isReady(video);
  }

  watch(id: string, video: HTMLVideoElement, refresh: () => void): void {
    if (this.has(id, video)) return;
    this.remove(id);
    const size = () => `${video.videoWidth}x${video.videoHeight}`;
    let lastSize = VideoFrameObserver.isReady(video) ? size() : "";
    const onFrame = () => {
      if (!VideoFrameObserver.isReady(video) || lastSize === size()) return;
      lastSize = size();
      refresh();
    };
    video.addEventListener("loadeddata", onFrame);
    video.addEventListener("resize", onFrame);
    this.videos.set(id, {
      video,
      dispose: () => {
        video.removeEventListener("loadeddata", onFrame);
        video.removeEventListener("resize", onFrame);
      },
    });
  }

  remove(id: string): void {
    this.videos.get(id)?.dispose();
    this.videos.delete(id);
  }

  clear(): void {
    for (const id of this.videos.keys()) this.remove(id);
  }
}
